'use client';

import { configKeepData5Mins } from '@/config/useQuery';
import { colorPairFromText } from '@/lib/color';
import { cn } from '@/lib/utils';
import { PublicBriefDepartmentResponse } from '@/server/api/v1/department/department.routes';
import {
    ChildLogicalOperator,
    FilterBuilders,
    LogicalOperator,
    OperatorType,
} from '@/server/base/base-trpc-service';
import { api } from '@/trpc/client';
import { DepartmentType } from '@/types/enum';
import { Download, Search } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';

const PublicUnitTable = () => {
    const transPage = useTranslations('PublicStatistics');

    // ── Search ────────────────────────────────────────────────────────────────
    const [keyword, setKeyword] = useState('');
    const [debouncedKeyword, setDebouncedKeyword] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setDebouncedKeyword(keyword.trim()), 300);
        return () => clearTimeout(t);
    }, [keyword]);

    const filters =
        useMemo((): FilterBuilders<PublicBriefDepartmentResponse>[] => {
            const base: FilterBuilders<PublicBriefDepartmentResponse>[] = [
                {
                    key: 'Type',
                    values: [DepartmentType.Department],
                    logicalOperator: LogicalOperator.And,
                    operatorType: OperatorType.Equal,
                },
            ];
            if (!debouncedKeyword) return base;
            return [
                ...base,
                {
                    logicalOperator: LogicalOperator.And,
                    childLogicalOperator: ChildLogicalOperator.Or,
                    filters: [
                        {
                            key: 'Name',
                            values: [debouncedKeyword],
                            logicalOperator: LogicalOperator.Or,
                            operatorType: OperatorType.Regex,
                        },
                        {
                            key: 'Code',
                            values: [debouncedKeyword],
                            logicalOperator: LogicalOperator.Or,
                            operatorType: OperatorType.Regex,
                        },
                    ],
                },
            ];
        }, [debouncedKeyword]);

    // ── API 1: danh sách phòng ban ────────────────────────────────────────────
    const { data: deptData, isLoading: isDeptLoading } =
        api.department.GetPublicBriefPage.useQuery(
            { pageNumber: 1, pageSize: 20, filters },
            configKeepData5Mins
        );

    const departments = useMemo(() => deptData?.data?.Items ?? [], [deptData]);
    const codes = useMemo(
        () => departments.map((d) => d.Code).filter((c): c is string => !!c),
        [departments]
    );

    // ── API 2: thống kê theo code đơn vị ─────────────────────────────────────
    const { data: statsData, isLoading: isStatsLoading } =
        api.dashboard.Query_GetUnitQualityStats.useQuery(codes, {
            ...configKeepData5Mins,
            enabled: codes.length > 0,
        });

    const statsMap = useMemo(() => {
        const map = new Map<
            string,
            { Total: number; OnTime: number; Overdue: number }
        >();
        statsData?.data?.forEach((s) => {
            map.set(s.DepartmentCode, {
                Total: s.Total,
                OnTime: s.OnTime,
                Overdue: s.Overdue,
            });
        });
        return map;
    }, [statsData]);

    const isLoading = isDeptLoading || (codes.length > 0 && isStatsLoading);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const getRateLabel = useCallback(
        (rate: number) => {
            if (rate >= 95) return transPage('PublicStatistics_StatusGood');
            if (rate >= 80) return transPage('PublicStatistics_StatusNormal');
            return transPage('PublicStatistics_StatusNeedImprovement');
        },
        [transPage]
    );

    const getRateBadge = (rate: number) => {
        if (rate >= 95)
            return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
        if (rate >= 80)
            return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
        return 'bg-red-50 text-red-700 ring-1 ring-red-200';
    };

    const getRateBar = (rate: number) => {
        if (rate >= 95) return 'bg-emerald-500';
        if (rate >= 80) return 'bg-amber-400';
        return 'bg-red-400';
    };

    // ── Rows: join + sort by Total desc ───────────────────────────────────────
    const rows = useMemo(() => {
        return departments
            .map((dept) => {
                const stats = statsMap.get(dept.Code ?? '') ?? {
                    Total: 0,
                    OnTime: 0,
                    Overdue: 0,
                };
                const rate =
                    stats.Total > 0
                        ? +((stats.OnTime / stats.Total) * 100).toFixed(1)
                        : 0;
                return { dept, stats, rate };
            })
            .sort((a, b) => b.stats.Total - a.stats.Total);
    }, [departments, statsMap]);

    // ── Export Excel ──────────────────────────────────────────────────────────
    const handleExport = useCallback(() => {
        const data = rows.map(({ dept, stats, rate }) => ({
            [transPage('PublicStatistics_Unit')]: dept.Name ?? '',
            [transPage('PublicStatistics_Total')]: stats.Total,
            [transPage('PublicStatistics_OnTime')]: stats.OnTime,
            [transPage('PublicStatistics_Overdue')]: stats.Overdue,
            [transPage('PublicStatistics_Rate')]: rate,
            [transPage('PublicStatistics_StatusLabel')]: getRateLabel(rate),
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Chất lượng đơn vị');
        XLSX.writeFile(wb, 'chat-luong-don-vi.xlsx');
    }, [rows, transPage, getRateLabel]);

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div>
            {/* Header: title + search (inline on md+) + export */}
            <div className='flex flex-col gap-2 border-b border-gray-50 px-5 py-3.5 md:flex-row md:items-center md:justify-between'>
                <p className='shrink-0 text-sm font-semibold text-gray-700'>
                    {transPage('PublicStatistics_UnitQualityTable')}
                </p>
                <div className='flex items-center gap-2'>
                    <div className='relative'>
                        <Search
                            size={13}
                            className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400'
                        />
                        <input
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            placeholder={transPage(
                                'PublicStatistics_SearchPlaceholder'
                            )}
                            className='w-56 rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-7 pr-3 text-xs placeholder:text-gray-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary'
                        />
                    </div>
                    <button
                        onClick={handleExport}
                        disabled={isLoading || rows.length === 0}
                        className='flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40'
                    >
                        <Download size={12} />
                        {transPage('PublicStatistics_ExportReport')}
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className='p-5'>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead>
                            <tr className='border-b border-gray-100'>
                                <th className='py-3 pr-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-400'>
                                    {transPage('PublicStatistics_Unit')}
                                </th>
                                <th className='px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-400'>
                                    {transPage('PublicStatistics_Total')}
                                </th>
                                <th className='px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-400'>
                                    {transPage('PublicStatistics_OnTime')}
                                </th>
                                <th className='px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-400'>
                                    {transPage('PublicStatistics_Overdue')}
                                </th>
                                <th className='px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-400'>
                                    {transPage('PublicStatistics_Rate')}
                                </th>
                                <th className='py-3 pl-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-400'>
                                    {transPage('PublicStatistics_StatusLabel')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-50'>
                            {isLoading
                                ? Array.from({ length: 5 }).map((_, i) => (
                                      <tr key={i}>
                                          {Array.from({ length: 6 }).map(
                                              (_, j) => (
                                                  <td
                                                      key={j}
                                                      className='px-4 py-3.5'
                                                  >
                                                      <div className='h-4 animate-pulse rounded bg-gray-100' />
                                                  </td>
                                              )
                                          )}
                                      </tr>
                                  ))
                                : rows.map(({ dept, stats, rate }) => {
                                      const isEmpty = stats.Total === 0;
                                      const colorObj =
                                          dept.Origin_Def_Code &&
                                          dept.Origin_Def_Code.length > 0
                                              ? colorPairFromText(
                                                    dept.Origin_Def_Code
                                                )
                                              : undefined;
                                      const firstCol = (
                                          <td
                                              className={cn(
                                                  isEmpty && 'opacity-80',
                                                  'flex items-center gap-2 py-3.5 pr-4'
                                              )}
                                          >
                                              {colorObj && (
                                                  <span
                                                      style={{
                                                          color: colorObj.fg,
                                                          background:
                                                              colorObj.bg,
                                                      }}
                                                      className={cn(
                                                          'rounded-full px-1.5 py-1 font-mono text-xs'
                                                      )}
                                                  >
                                                      {dept.Origin_Def_Code}
                                                  </span>
                                              )}

                                              <span
                                                  className={cn(
                                                      'font-medium',
                                                      !isEmpty &&
                                                          'group-hover:text-primary'
                                                  )}
                                              >
                                                  {dept.Name}
                                              </span>
                                          </td>
                                      );
                                      if (isEmpty)
                                          return (
                                              <tr
                                                  key={dept.ID}
                                                  className={cn(
                                                      'transition-colors'
                                                  )}
                                              >
                                                  {firstCol}
                                                  <td
                                                      colSpan={5}
                                                      className='py-3.5 pl-4 text-center'
                                                  >
                                                      <span className='text-xs italic text-gray-400'>
                                                          {transPage(
                                                              'PublicStatistics_UnitNoData'
                                                          )}
                                                      </span>
                                                  </td>
                                              </tr>
                                          );

                                      return (
                                          <tr
                                              key={dept.ID}
                                              className={cn(
                                                  'transition-colors',
                                                  'group hover:bg-gray-50/60'
                                              )}
                                          >
                                              {firstCol}
                                              <td className='px-4 py-3.5 text-right tabular-nums text-gray-600'>
                                                  {stats.Total}
                                              </td>
                                              <td className='px-4 py-3.5 text-right font-medium tabular-nums text-emerald-600'>
                                                  {stats.OnTime}
                                              </td>
                                              <td className='px-4 py-3.5 text-right font-medium tabular-nums text-red-500'>
                                                  {stats.Overdue}
                                              </td>
                                              <td className='px-4 py-3.5 text-right'>
                                                  <div className='flex items-center justify-end gap-2'>
                                                      <div className='h-1.5 w-16 overflow-hidden rounded-full bg-gray-100'>
                                                          <div
                                                              className={`h-full rounded-full ${getRateBar(rate)}`}
                                                              style={{
                                                                  width: `${rate}%`,
                                                              }}
                                                          />
                                                      </div>
                                                      <span className='w-12 text-right text-xs font-semibold text-gray-700'>
                                                          {rate}%
                                                      </span>
                                                  </div>
                                              </td>
                                              <td className='py-3.5 pl-4'>
                                                  <span
                                                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getRateBadge(rate)}`}
                                                  >
                                                      {getRateLabel(rate)}
                                                  </span>
                                              </td>
                                          </tr>
                                      );
                                  })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PublicUnitTable;
